import React from "react";
import * as arrow from "apache-arrow";
import { usePerspective } from "../../contexts/app/provider";
import { parseParquetData, isChunkedParsedData } from "../../utils/parquet";
import { GetTransformedRawParquetDocument } from "../../services/perspective_api/__generated__/perspective_server-types";

export type SkuCatalogEntry = {
  sku: string;
  location: string;
  skuName: string;
  supplier: string;
  cost: number | null;
  qtyPerCase: number | null;
  skuStatus: string | null;
  /** Minimum order quantity for the lane, when the master carries one. */
  moq: number | null;
  /** Order and delivery dates of the cycle the inbox is showing, ISO. */
  orderDate: string;
  deliveryDate: string;
};

export type SkuCatalogState = {
  entries: SkuCatalogEntry[];
  loading: boolean;
  error: string | null;
  /** Re-runs the fetch after a failure — without it the error is terminal, since
   * the caller's "requested" flag is latched and the effect never fires again. */
  retry: () => void;
};

type CatalogData = Omit<SkuCatalogState, "retry">;

const EMPTY: CatalogData = { entries: [], loading: false, error: null };

const cellToString = (value: unknown): string =>
  value === null || value === undefined ? "" : String(value);

// Arrow hands timestamps back as Date, epoch millis or a string depending on
// the column; the save wants an ISO string.
const cellToDate = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "";
  if (value instanceof Date) return value.toISOString();
  if (typeof value === "number" || typeof value === "bigint") {
    return new Date(Number(value)).toISOString();
  }
  const d = new Date(String(value));
  return Number.isNaN(d.getTime()) ? String(value) : d.toISOString();
};

const cellToNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const tableToEntries = (table: arrow.Table): SkuCatalogEntry[] => {
  const entries: SkuCatalogEntry[] = [];
  for (let i = 0; i < table.numRows; i++) {
    const row = table.get(i);
    if (!row) continue;
    const sku = cellToString(row.sku);
    const location = cellToString(row.location);
    if (!sku || !location) continue;
    entries.push({
      sku,
      location,
      skuName: cellToString(row.sku_name),
      supplier: cellToString(row.supplier),
      cost: cellToNumber(row.cost),
      qtyPerCase: cellToNumber(row.qty_per_case),
      skuStatus:
        row.sku_status === null || row.sku_status === undefined
          ? null
          : String(row.sku_status),
      moq: cellToNumber(row.moq),
      orderDate: cellToDate(row.order_date),
      deliveryDate: cellToDate(row.delivery_date),
    });
  }
  return entries;
};

/**
 * Orderable SKUs for the "add a SKU" picker, from the `sku_catalog` transform.
 * The page's own dataset only carries SKUs the engine already planned, which is
 * exactly the set a planner adding an unsuggested line needs to look past.
 *
 * Fetched lazily — `enabled` stays false until the dialog is first opened, so
 * the extra transform never runs for planners who don't use the feature.
 */
export const useSkuCatalog = (
  runSettings: unknown | null,
  enabled: boolean,
): SkuCatalogState => {
  const { perspectiveApiService } = usePerspective();
  const [state, setState] = React.useState<CatalogData>(EMPTY);
  const [attempt, setAttempt] = React.useState(0);
  const retry = React.useCallback(() => setAttempt((n) => n + 1), []);

  const runSettingsKey = React.useMemo(
    () => (runSettings ? JSON.stringify(runSettings) : null),
    [runSettings],
  );

  React.useEffect(() => {
    if (!enabled) return;
    if (!runSettingsKey) {
      // Returning without touching state would leave the initial
      // {loading:false, entries:[]} in place, which the dialog renders as a
      // fully-loaded picker showing "0 suppliers" — no spinner, no error, no
      // retry, and nothing to tell the planner the run simply is not ready.
      setState({ entries: [], loading: false, error: "No run selected" });
      return;
    }

    let cancelled = false;
    setState({ entries: [], loading: true, error: null });

    // The transform sources atomic_master_enriched itself; po_plan_edit is
    // passed only to satisfy the base-dataset argument, matching how the
    // GoodChop CSV export transforms are invoked.
    const variables = {
      datasetName: "po_plan_edit",
      transformId: "goodchop_sku_catalog",
      folder: "outputs",
      runSettingsJson: JSON.parse(runSettingsKey),
      comparedToRunSettingsJson: null,
      format: "parquet" as const,
      params: { group_by_fields: [], filters: [] },
    };

    let pendingError: string | null = null;
    let sawData = false;
    // The subscription emits more than once (cached, then fresh) and each emit
    // parses asynchronously, so they can finish out of order — a large cached
    // base64 payload decodes slower than a small fresh one. `cancelled` only
    // covers teardown, so without a sequence the slow STALE emit lands last and
    // overwrites the fresh catalog the planner is about to pick from.
    let latestEmit = 0;

    const client = perspectiveApiService.getClient();
    const sub = client
      .subscribe({ query: GetTransformedRawParquetDocument, variables })
      .subscribe({
        next: async (res: any) => {
          const payload = res?.data?.getTransformedRawParquet;
          const parquetData = payload?.parquetData;
          // The server's first emit replays cached state, which may be a stale
          // failure that the fresh run supersedes; latch it and let `complete`
          // decide, the same way the nested-details subscription does.
          if (payload?.status === "FAILED" || payload?.errorMessage) {
            pendingError = String(
              payload?.errorMessage || "Transformation failed",
            );
            return;
          }
          if (!parquetData || typeof parquetData !== "string") return;
          sawData = true;
          pendingError = null;
          const emit = ++latestEmit;
          try {
            const parsed = await parseParquetData(
              parquetData,
              !parquetData.startsWith("http"),
            );
            if (cancelled || emit !== latestEmit) return;
            if (isChunkedParsedData(parsed)) {
              setState({
                entries: [],
                loading: false,
                error: "SKU catalog too large to load",
              });
              return;
            }
            setState({
              entries: tableToEntries(parsed.arrowTable),
              loading: false,
              error: null,
            });
          } catch (e) {
            // Same guard: a late failure from a superseded emit must not blank
            // a catalog that already loaded successfully.
            if (!cancelled && emit === latestEmit) {
              setState({
                entries: [],
                loading: false,
                error:
                  e instanceof Error
                    ? e.message
                    : "Failed to parse SKU catalog",
              });
            }
          }
        },
        error: (e: any) => {
          if (cancelled) return;
          setState({
            entries: [],
            loading: false,
            error: e?.message || "Failed to load SKU catalog",
          });
        },
        complete: () => {
          if (cancelled || sawData) return;
          setState({
            entries: [],
            loading: false,
            error: pendingError || "No SKU catalog returned",
          });
        },
      });

    return () => {
      cancelled = true;
      sub.unsubscribe();
    };
  }, [enabled, runSettingsKey, perspectiveApiService, attempt]);

  return React.useMemo(() => ({ ...state, retry }), [state, retry]);
};
